import React, { useEffect, useState, forwardRef } from 'react';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '../ui/tooltip';
import { useToolsStore } from '../../store/toolsStore';
import { useCreditsStore } from '../../store/creditsStore';
import { Coins, ArrowRight, Lightbulb, Info } from 'lucide-react';
import NodeChip from './NodeChip';
import NodeBusyBadge from './NodeBusyBadge';
import NodeMoreInGallery from './NodeMoreInGallery';
import NodePriceBadge from './NodePriceBadge';
import { guardTriggerProps } from '../../utils/portalGuard';

/**
 * Small helper that renders the rich tooltip body for a given tool id.
 * If the tool is not (yet) in the registry cache we show a minimal fallback.
 * Exported so the Tool Palette can render the same body next to its items.
 *
 * Envío 4: con el precio dinámico prendido el tooltip cotiza con los
 * parámetros por defecto del nodo (/credits/quote); NUNCA muestra el costo
 * fijo del manifiesto para un tipo con result_type. Con el flag apagado el
 * endpoint devuelve el fijo: mismo número de siempre.
 */
export const NodeTooltipBody = ({ toolId }) => {
  const tool = useToolsStore((s) => s.toolsById[toolId]);
  const fetchQuote = useCreditsStore((s) => s.fetchQuote);
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    let alive = true;
    if (tool?.result_type) {
      fetchQuote(tool.result_type, {}).then((q) => { if (alive) setQuote(q); });
    }
    return () => { alive = false; };
  }, [tool?.result_type, fetchQuote]);

  if (!tool) {
    return (
      <div className="node-tt-body">
        <div className="node-tt-header">
          <Info size={14} />
          <span className="node-tt-title">{toolId}</span>
        </div>
        <p className="node-tt-desc">Sin descripción disponible.</p>
      </div>
    );
  }

  const inputs = tool.inputs || [];
  const outputs = tool.outputs || [];
  const examples = (tool.examples || []).slice(0, 3);

  return (
    <div className="node-tt-body" data-testid={`node-tooltip-${toolId}`}>
      <div className="node-tt-header">
        <span className="node-tt-title">{tool.display || tool.id}</span>
        {typeof tool.cost === 'number' && tool.cost > 0 && (
          <span className="node-tt-cost" title="Costo en créditos"
                data-testid={`tooltip-cost-${toolId}`}>
            <Coins size={11} />
            {!tool.result_type
              ? tool.cost
              : quote == null
                ? '…'
                : quote.mode === 'blocked'
                  ? 'sin precio'
                  : quote.mode === 'max'
                    ? `hasta ${quote.credits}`
                    : quote.credits}
          </span>
        )}
      </div>

      <p className="node-tt-desc">{tool.description}</p>

      {(inputs.length > 0 || outputs.length > 0) && (
        <div className="node-tt-io">
          {inputs.length > 0 && (
            <div className="node-tt-io-row">
              <span className="node-tt-io-label">Entrada:</span>
              <span className="node-tt-io-pills">
                {inputs.map((io, i) => (
                  <span key={i} className={`node-tt-pill ${io.required ? '' : 'optional'}`}>
                    {io.type.replace('_url', '')}{io.required ? '' : '?'}
                  </span>
                ))}
              </span>
            </div>
          )}
          {outputs.length > 0 && (
            <div className="node-tt-io-row">
              <ArrowRight size={11} className="node-tt-io-arrow" />
              <span className="node-tt-io-label">Salida:</span>
              <span className="node-tt-io-pills">
                {outputs.map((io, i) => (
                  <span key={i} className="node-tt-pill out">
                    {io.type.replace('_url', '')}
                  </span>
                ))}
              </span>
            </div>
          )}
        </div>
      )}

      {examples.length > 0 && (
        <div className="node-tt-examples">
          <div className="node-tt-examples-header">
            <Lightbulb size={11} />
            <span>Ejemplos</span>
          </div>
          <ul>
            {examples.map((ex, i) => (
              <li key={i}>{ex}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/**
 * Anchor wrapper — forwards ref + event handlers from Radix Trigger
 * down onto the node's outer div, so hover works without breaking
 * React Flow's dragging.
 */
const NodeTooltipAnchor = forwardRef(function NodeTooltipAnchor(
  { children, ...triggerProps },
  ref
) {
  const guarded = guardTriggerProps(triggerProps);
  return (
    <div ref={ref} className="node-tooltip-anchor" {...guarded}>
      {children}
    </div>
  );
});

/**
 * Higher-order wrapper: given a React Flow node component and its type id,
 * wrap it in a Radix Tooltip that reveals the rich body after a 500ms hover.
 */
export const withNodeTooltip = (NodeComponent, toolId) => {
  const Wrapped = (props) => {
    // Lazily fetch the catalog the first time any tooltip is used.
    const fetchTools = useToolsStore((s) => s.fetchTools);
    const loadedAt = useToolsStore((s) => s.loadedAt);
    useEffect(() => {
      if (!loadedAt) fetchTools();
    }, [loadedAt, fetchTools]);

    return (
      <Tooltip delayDuration={500} disableHoverableContent>
        <TooltipTrigger asChild>
          <NodeTooltipAnchor>
            {/* Chip "#21 · nombre" (E5): va acá para que TODOS los nodos lo
                tengan sin tocar sus 29 componentes. */}
            <NodeChip nodeId={props.id} />
            {/* E6c.5 · generaciones en curso del nodo + aviso de galería.
                También acá para que lo tengan los 25 nodos sin tocarlos. */}
            <NodeBusyBadge nodeId={props.id} />
            <NodeMoreInGallery nodeId={props.id} />
            {/* Envío 4 · precio del nodo: siempre de /credits/quote, con
                debounce al cambiar duración/resolución/entradas. */}
            <NodePriceBadge nodeId={props.id} toolId={toolId} />
            {/* si el nodo cambió desde afuera (LMX, Recargar, otra pestaña)
                sube __extRev y el nodo se remonta leyendo el dato nuevo */}
            <NodeComponent key={props.data?.__extRev || 0} {...props} />
          </NodeTooltipAnchor>
        </TooltipTrigger>
        <TooltipContent
          className="node-rich-tooltip"
          side="right"
          align="start"
          sideOffset={14}
          collisionPadding={16}
        >
          <NodeTooltipBody toolId={toolId} />
        </TooltipContent>
      </Tooltip>
    );
  };
  Wrapped.displayName = `WithTooltip(${NodeComponent.displayName || NodeComponent.name || toolId})`;
  return Wrapped;
};

export default withNodeTooltip;
